import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Link,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import DescriptionIcon from '@mui/icons-material/Description';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import LoginIcon from '@mui/icons-material/Login';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useAuth } from '../context/AuthContext';

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, schedulingEnabled } = useAuth();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Container component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            How to Use This Site
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ mt: 3, mb: 4 }}>
            Welcome to the Reporter Portal. The following resources will guide you through 
            the process of navigating this site and completing your worksheet submission.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Login Video Tutorial */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                borderLeft: '4px solid', 
                borderColor: 'info.main',
                transition: 'box-shadow 0.3s',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LoginIcon sx={{ mr: 2, color: 'info.main', fontSize: 30 }} />
                <Typography variant="h6" component="h2">
                  How to Login
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Learn how to sign in to the Reporter Portal using your email and secure token.
              </Typography>
              <Link 
                href="/assets/Reporter_Portal_Login.mp4" 
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
              >
                <Button 
                  variant="contained" 
                  color="info"
                  startIcon={<PlayCircleOutlineIcon />}
                >
                  Watch Login Tutorial
                </Button>
              </Link>
            </Paper>

            {/* PDF Guide */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                borderLeft: '4px solid', 
                borderColor: 'primary.main',
                transition: 'box-shadow 0.3s',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 2, color: 'primary.main', fontSize: 30 }} />
                <Typography variant="h6" component="h2">
                  Worksheet Submission Step-by-Step Guide (PDF)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                A comprehensive written guide that walks you through each step of the 
                worksheet submission process.
              </Typography>
              <Link 
                href="/assets/Reporter Worksheet Step-by-Step Guide.pdf" 
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
              >
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<DescriptionIcon />}
                >
                  View PDF Guide
                </Button>
              </Link>
            </Paper>

            {/* Worksheet Video Tutorial */}
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3, 
                borderLeft: '4px solid', 
                borderColor: 'secondary.main',
                transition: 'box-shadow 0.3s',
                '&:hover': {
                  boxShadow: 3
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PlayCircleOutlineIcon sx={{ mr: 2, color: 'secondary.main', fontSize: 30 }} />
                <Typography variant="h6" component="h2">
                  Worksheet Submission Tutorial
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Watch a video demonstration showing how to navigate the site and 
                complete your worksheet submission.
              </Typography>
              <Link 
                href="/assets/Reporter_Worksheet_Demo.mp4" 
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
              >
                <Button 
                  variant="contained" 
                  color="secondary"
                  startIcon={<PlayCircleOutlineIcon />}
                >
                  Watch Worksheet Demo
                </Button>
              </Link>
            </Paper>

            {/* Availability Video Tutorial - Only show if user is authenticated and has scheduling enabled */}
            {isAuthenticated && schedulingEnabled && (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 3, 
                  borderLeft: '4px solid', 
                  borderColor: 'success.main',
                  transition: 'box-shadow 0.3s',
                  '&:hover': {
                    boxShadow: 3
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarMonthIcon sx={{ mr: 2, color: 'success.main', fontSize: 30 }} />
                  <Typography variant="h6" component="h2">
                    Managing Your Availability
                  </Typography>
                </Box>
                <Typography variant="body2" paragraph>
                  Learn how to set and manage your availability for virtual and in-person depositions.
                </Typography>
                <Link 
                  href="/assets/Reporter_Portal_Availability.mp4" 
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="none"
                >
                  <Button 
                    variant="contained" 
                    color="success"
                    startIcon={<PlayCircleOutlineIcon />}
                  >
                    Watch Availability Tutorial
                  </Button>
                </Link>
              </Paper>
            )}
          </Box>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" align="center">
              If you need additional assistance, please use the Contact Us link in the header 
              to reach our support team.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default HelpPage;